import { computed, ref, toValue } from 'vue'
import AppMessage from '@/components/message/index.js'
import xbb from '@xbb/xbb-utils'
import utils from '@/utils'
import { shareFile } from '@/api/new-agent/qca.js'

const { WEBPACK_ADDRESS_URL } = process.env

const ILLEGAL_FILENAME_CHARS = /[\\/:*?"<>|]/g
const FALLBACK_FILENAME = '分析报告'
const LOG_TAG = '[panel-file-actions]'

// 各文件类型的后缀补全规则：命中 pattern 时沿用原名，否则补上 suffix
const EXTENSION_RULES = {
  html: { pattern: /\.html?$/i, suffix: '.html' },
  md: { pattern: /\.(md|markdown)$/i, suffix: '.md' }
}

/**
 * 文件面板的公共操作：文件名展示、分享、下载。
 * @param {import('vue').MaybeRefOrGetter<Object>} panelData 面板文件数据
 * @param {{ ext?: import('vue').MaybeRefOrGetter<string> }} options ext 指定下载后缀规则，留空则不补后缀
 */
export function usePanelFileActions(panelData, options = {}) {
  const { ext = '' } = options

  const filename = computed(() => toValue(panelData)?.filename || '')

  const showTime = computed(() =>
    xbb.formatTime(toValue(panelData)?.updateTime || '', 'yyyy-MM-dd HH:mm:ss')
  )

  // 下载进行中，用于置灰按钮避免重复点击
  const downloading = ref(false)

  /** 生成安全的下载文件名：过滤非法字符，并按类型补全后缀。 */
  const buildDownloadFilename = () => {
    const rawName = String(toValue(panelData)?.filename || '').trim()
    const safeName = (rawName.replace(ILLEGAL_FILENAME_CHARS, '_') || FALLBACK_FILENAME).slice(
      0,
      100
    )
    const rule = EXTENSION_RULES[toValue(ext)]
    if (!rule) return safeName
    return rule.pattern.test(safeName) ? safeName : `${safeName}${rule.suffix}`
  }

  /**
   * 通过临时 <a download> 触发浏览器下载。
   * 跨域链接的 download 属性会被忽略，因此走链接下载时开新标签，避免当前页面被替换。
   */
  const triggerDownload = (href, fileName, openInNewTab = false) => {
    const link = document.createElement('a')
    link.href = href
    link.download = fileName
    if (openInNewTab) {
      link.target = '_blank'
      link.rel = 'noopener'
    }
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  /** 获取分享链接，供复制和系统分享复用。 */
  const getShareUrl = async () => {
    const data = toValue(panelData)
    if (data?.mimeType === 'text/markdown') {
      if (!data.fileUrl) {
        AppMessage.warning('报告尚未生成，暂时无法分享')
        return ''
      }
      return `${WEBPACK_ADDRESS_URL}/markdown-preview.html?url=${encodeURIComponent(data.fileUrl)}&filename=${encodeURIComponent(data.filename || '')}`
    }
    if (!data?.fileId) {
      AppMessage.warning('缺少文件信息，暂时无法分享')
      return ''
    }
    try {
      const res = await shareFile({ fileId: data.fileId })
      const url = res?.result?.url
      if (!url) throw new Error('Missing share URL')
      return url
    } catch (error) {
      console.error(`${LOG_TAG} share failed`, error)
      AppMessage.error('获取分享链接失败')
      return ''
    }
  }

  const copyShareLink = async (url) => {
    if (!url) return false
    try {
      await new Promise((resolve, reject) => {
        utils.copyToClipboard(url, resolve, reject)
      })
      AppMessage.success('分享链接已复制')
      return true
    } catch (error) {
      AppMessage.error('复制分享链接失败，请重试')
      return false
    }
  }

  const shareWithSystem = async (url) => {
    if (!url) return false
    if (!window.plus?.share?.sendWithSystem) {
      AppMessage.warning('当前环境不支持系统分享，请复制链接')
      return false
    }
    try {
      await new Promise((resolve, reject) => {
        window.plus.share.sendWithSystem(
          { type: 'text', content: '分享内容', href: url },
          resolve,
          reject
        )
      })
      return true
    } catch (error) {
      return false
    }
  }

  const shareWithWeixin = async (url) => {
    if (!url) return false
    if (!window.plus?.share?.getServices) {
      AppMessage.warning('当前环境不支持微信分享，请在 App 中使用或复制链接')
      return false
    }
    try {
      const services = await new Promise((resolve, reject) => {
        window.plus.share.getServices(resolve, reject)
      })
      const weixinShare = services.find((service) => service.id === 'weixin')
      if (!weixinShare) {
        AppMessage.warning('当前 App 暂不支持微信分享，请复制链接')
        return false
      }
      if (weixinShare.nativeClient === false) {
        AppMessage.warning('请先安装微信后再分享')
        return false
      }
      await new Promise((resolve, reject) => {
        weixinShare.send(
          {
            type: 'text',
            content: url,
            extra: { scene: 'WXSceneSession' }
          },
          resolve,
          reject
        )
      })
      return true
    } catch (error) {
      console.error(`${LOG_TAG} weixin share failed`, error)
      AppMessage.error('微信分享未完成，请重试')
      return false
    }
  }

  // 保留已有桌面面板点击分享即复制链接的行为。
  const handleShare = async () => {
    const url = await getShareUrl()
    return copyShareLink(url)
  }

  /**
   * 下载面板文件。
   * 优先 fetch 成 blob 后本地保存，保证文件名与后缀可控；
   * 跨域或不支持 CORS 时退化为直接用链接下载（此时文件名以服务端返回为准）。
   */
  const handleDownload = async () => {
    const fileUrl = toValue(panelData)?.fileUrl
    if (!fileUrl) {
      AppMessage.warning('报告尚未生成，暂时无法下载')
      return
    }
    if (downloading.value) return

    const fileName = buildDownloadFilename()
    downloading.value = true
    try {
      const res = await fetch(fileUrl, { credentials: 'omit' })
      if (!res.ok) throw new Error(`download failed with status ${res.status}`)
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      triggerDownload(objectUrl, fileName)
      // 立即 revoke 可能导致部分浏览器下载中断，延后释放
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
      AppMessage.success('下载成功')
    } catch (error) {
      console.error(`${LOG_TAG} download failed`, error)
      triggerDownload(fileUrl, fileName, true)
    } finally {
      downloading.value = false
    }
  }

  return {
    filename,
    showTime,
    downloading,
    handleDownload,
    handleShare,
    getShareUrl,
    copyShareLink,
    shareWithSystem,
    shareWithWeixin
  }
}
